import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  TableRowActionsMenu,
  ConfirmDialog,
  Typography,
  useConfirmDialog,
} from '@occc/ui-design-system';
import { snackbar } from '@occc-app/design-system';
import { useProductCategoryDeleteMutation } from '@occc-app/dataAccessLayer/productCategory/mutations';
import { ProductCategoryFragment } from '@occc/graphql';

import { routeCreator } from '../utils';

export type ProductCategoryTableRowActionsProps = {
  productCategory: ProductCategoryFragment;
};

export const ProductCategoryTableRowActions: React.FC<ProductCategoryTableRowActionsProps> = ({
  productCategory,
}) => {
  const navigate = useNavigate();

  const onDeleteComplete = () => {
    snackbar.success('Product Preference has been successfully deleted.');
  };

  const [productCategoryDelete] = useProductCategoryDeleteMutation({
    onCompleted: onDeleteComplete,
    refetchQueries: ['productCategories'],
  });

  const onDelete = () => productCategoryDelete({ variables: { id: productCategory.id } });

  const { open, setOpen, onCloseButtonClick, onConfirmButtonClick } = useConfirmDialog({
    onConfirm: onDelete,
    shouldStopEventPropagation: true,
  });

  const menuItems = useMemo(
    () => [
      {
        label: 'Edit Product Preference',
        onClick: () => navigate(routeCreator.update(productCategory.id)),
        dataTestId: 'edit',
      },
      {
        label: 'Delete Product Preference',
        onClick: () => setOpen(true),
        dataTestId: 'delete',
      },
    ],
    [navigate, productCategory.id, setOpen],
  );

  return (
    <>
      <TableRowActionsMenu menuItems={menuItems} />
      <ConfirmDialog
        dialogTitle='Are you sure?'
        open={open}
        onClose={onCloseButtonClick}
        confirmButtonText='Confirm'
        confirmButtonProps={{
          onClick: onConfirmButtonClick,
          color: 'error',
        }}
      >
        <Typography>
          This action will delete the Product Preference "{productCategory.name}".
        </Typography>
      </ConfirmDialog>
    </>
  );
};
